import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../../lib/api';

interface Release {
  id: string;
  name: string | null;
  playSlug: string;
  publishedAt: string;
  playUrl: string;
}

type Visibility = 'private' | 'public';

interface PublishState {
  status: 'draft' | 'published';
  visibility: Visibility;
  thumbnailUrl: string | null;
  title: string;
}

interface PublishDialogProps {
  projectId: string;
  project: PublishState;
  onClose: () => void;
  onChanged: () => void;
}

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: Visibility;
  label: string;
  hint: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'public',
    label: 'Public',
    hint: 'Listed in the playstore — anyone can find and play it',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
    ),
  },
  {
    value: 'private',
    label: 'Private',
    hint: 'Hidden from the playstore — only people with the link can play',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
    ),
  },
];

/**
 * Publishing is native (spec D10): one dialog owns the whole release story — cover, who can see it,
 * and whether it is live. A release pins a version, so what people play is exactly what was tested.
 *
 * The three controls map straight onto the data model: thumbnail (cover on every card), visibility
 * (public = playstore listing, private = unguessable link only) and status (published = the link
 * works at all; unpublishing kills the link AND the listing).
 */
export function PublishDialog({ projectId, project, onClose, onChanged }: PublishDialogProps) {
  const [releases, setReleases] = useState<Release[]>([]);
  const [state, setState] = useState<PublishState>(project);
  const [visibility, setVisibility] = useState<Visibility>(project.visibility);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState<'generate' | 'upload' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number>();
  const fileInput = useRef<HTMLInputElement>(null);

  const isPublished = state.status === 'published';
  const latest = releases[0];

  const copyLink = (url: string) => {
    void navigator.clipboard?.writeText(url);
    setCopied(true);
    window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
  };

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ project: PublishState | null; releases: Release[] }>(
          `/box/projects/${projectId}/publish`,
        );
        setReleases(data.releases);
        if (data.project) {
          setState(data.project);
          setVisibility(data.project.visibility);
        }
      } catch {
        setError('Could not load your published versions');
      }
    })();
  }, [projectId]);

  useEffect(() => {
    if (!latest || !isPublished) {
      setQr(null);
      return;
    }
    void QRCode.toDataURL(latest.playUrl, { width: 160, margin: 1 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [latest, isPublished]);

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await api.post<{ release: Release; playUrl: string }>(
        `/box/projects/${projectId}/publish`,
        { name: name.trim() || undefined, visibility },
      );
      setReleases(prev => [{ ...data.release, playUrl: data.playUrl }, ...prev]);
      setState(prev => ({ ...prev, status: 'published', visibility }));
      setName('');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not publish');
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    setBusy(true);
    try {
      await api.delete(`/box/projects/${projectId}/publish`);
      onChanged();
      onClose();
    } catch {
      setError('Could not unpublish');
    } finally {
      setBusy(false);
    }
  };

  /** Before first publish the choice rides along on POST; after it, it saves immediately. */
  const chooseVisibility = async (next: Visibility) => {
    setVisibility(next);
    if (!isPublished || next === state.visibility) return;
    setError(null);
    try {
      await api.patch(`/box/projects/${projectId}/publish/settings`, { visibility: next });
      setState(prev => ({ ...prev, visibility: next }));
      onChanged();
    } catch {
      setVisibility(state.visibility);
      setError('Could not change who can see it');
    }
  };

  const generateCover = async () => {
    setCoverBusy('generate');
    setError(null);
    try {
      const data = await api.post<{ thumbnailUrl: string }>(
        `/box/projects/${projectId}/publish/thumbnail`,
        { generate: true },
      );
      setState(prev => ({ ...prev, thumbnailUrl: data.thumbnailUrl }));
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not make a cover');
    } finally {
      setCoverBusy(null);
    }
  };

  const uploadCover = async (file: File) => {
    if (file.size > 5_000_000) {
      setError('That image is over 5 MB — pick a smaller one');
      return;
    }
    setCoverBusy('upload');
    setError(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
      });
      const data = await api.post<{ thumbnailUrl: string }>(
        `/box/projects/${projectId}/publish/thumbnail`,
        { dataUrl },
      );
      setState(prev => ({ ...prev, thumbnailUrl: data.thumbnailUrl }));
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload that image');
    } finally {
      setCoverBusy(null);
    }
  };

  return (
    /* Matches the studio language: borderless surface, token radii, modal-in entrance — the old
       version was a bordered white box that read as a leftover default dialog. */
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Publish"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-in flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-card bg-surface shadow-card">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div>
            <h2 className="text-base font-bold">{isPublished ? 'Share your build' : 'Publish your build'}</h2>
            <p className="mt-0.5 text-sm text-muted">
              {isPublished
                ? state.visibility === 'public'
                  ? 'Live in the playstore, and anyone with the link can play.'
                  : 'Live for anyone with the link — not listed in the playstore.'
                : 'Pick a cover, choose who can see it, then go live.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="min-h-0 space-y-4 overflow-y-auto p-5">
          {error && (
            <p className="rounded-field bg-danger/10 px-3 py-2 text-sm font-medium text-danger">{error}</p>
          )}

          {/* -------------------------------- cover -------------------------------- */}
          <div>
            <span className="mb-1.5 block text-xs font-semibold text-muted">Cover</span>
            <div className="flex items-stretch gap-3">
              <div className="relative aspect-[16/10] w-40 shrink-0 overflow-hidden rounded-field bg-gradient-to-br from-accent/25 via-accent/10 to-transparent">
                {state.thumbnailUrl ? (
                  <img
                    src={state.thumbnailUrl}
                    alt="Project cover"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="grid h-full w-full place-items-center text-2xl font-black text-accent-deep opacity-60">
                    {state.title.slice(0, 1).toUpperCase()}
                  </span>
                )}
                {coverBusy && (
                  <span className="absolute inset-0 grid place-items-center bg-surface/70 text-[11px] font-bold text-ink backdrop-blur-[1px]">
                    {coverBusy === 'generate' ? 'Drawing…' : 'Uploading…'}
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col justify-center gap-2">
                <button
                  type="button"
                  onClick={() => void generateCover()}
                  disabled={coverBusy !== null}
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-accent px-3 text-xs font-bold text-accent-ink transition hover:brightness-110 disabled:opacity-50"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" /></svg>
                  {state.thumbnailUrl ? 'New AI cover' : 'AI cover'}
                </button>
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  disabled={coverBusy !== null}
                  className="inline-flex h-8 items-center justify-center rounded-lg bg-surface px-3 text-xs font-bold text-ink shadow-sm transition hover:bg-wash disabled:opacity-50"
                >
                  Upload
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void uploadCover(file);
                  }}
                />
              </div>
            </div>
          </div>

          {/* ---------------------------- accessibility ---------------------------- */}
          <div>
            <span className="mb-1.5 block text-xs font-semibold text-muted">Who can see it</span>
            <div className="space-y-1.5" role="radiogroup" aria-label="Who can see it">
              {VISIBILITY_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={visibility === option.value}
                  onClick={() => void chooseVisibility(option.value)}
                  className={[
                    'flex w-full items-center gap-3 rounded-field px-3.5 py-2.5 text-left transition',
                    visibility === option.value
                      ? 'bg-accent/15 ring-1 ring-accent/60'
                      : 'bg-field ring-1 ring-transparent hover:bg-page',
                  ].join(' ')}
                >
                  <span className={visibility === option.value ? 'text-accent-deep' : 'text-muted'}>
                    {option.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold">{option.label}</span>
                    <span className="block text-xs text-muted">{option.hint}</span>
                  </span>
                  <span
                    className={[
                      'grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 transition',
                      visibility === option.value ? 'border-accent bg-accent' : 'border-line',
                    ].join(' ')}
                  >
                    {visibility === option.value && <span className="h-1.5 w-1.5 rounded-full bg-accent-ink" />}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* ------------------------------- live now ------------------------------ */}
          {isPublished && latest && (
            <div className="rounded-field bg-page/70 p-4">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                </span>
                <p className="text-sm font-bold">Live now{latest.name ? ` — ${latest.name}` : ''}</p>
                <span className="ml-auto rounded-full bg-surface px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-muted">
                  {state.visibility}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-4">
                {qr && (
                  <img src={qr} alt="QR code for the play link" className="h-24 w-24 shrink-0 rounded-lg bg-white p-1.5 shadow-sm" />
                )}
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="break-all font-mono text-[11px] leading-snug text-muted">{latest.playUrl}</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => copyLink(latest.playUrl)}
                      className="inline-flex h-8 items-center rounded-lg bg-accent px-3 text-xs font-bold text-accent-ink transition hover:brightness-110"
                    >
                      {copied ? 'Copied!' : 'Copy link'}
                    </button>
                    <a
                      href={latest.playUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-8 items-center rounded-lg bg-surface px-3 text-xs font-bold text-ink shadow-sm transition hover:bg-wash"
                    >
                      Open
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ------------------------------- publish ------------------------------- */}
          <div className="flex items-end gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-xs font-semibold text-muted">Name this version (optional)</span>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="First playable"
                className="w-full rounded-field bg-field px-3.5 py-2.5 text-sm outline-none ring-1 ring-transparent transition focus:ring-accent/50"
              />
            </label>
            <button
              type="button"
              onClick={() => void publish()}
              disabled={busy}
              className="inline-flex h-[42px] items-center rounded-field bg-accent px-4 text-sm font-bold text-accent-ink transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? 'Publishing…' : isPublished ? 'Publish update' : 'Publish'}
            </button>
          </div>

          {releases.length > 1 && (
            <div className="rounded-field bg-page/70 px-4 py-3">
              <h3 className="mb-1.5 text-[11px] font-extrabold uppercase tracking-wider text-muted">Earlier versions</h3>
              <ul className="space-y-1.5">
                {releases.slice(1, 6).map(release => (
                  <li key={release.id} className="flex items-center justify-between text-xs">
                    <span className="font-semibold">{release.name || 'Untitled'}</span>
                    <span className="tabular-nums text-muted">
                      {new Date(release.publishedAt).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isPublished && (
            <button
              type="button"
              onClick={() => void unpublish()}
              disabled={busy}
              className="text-xs font-semibold text-muted transition hover:text-danger disabled:opacity-50"
            >
              Unpublish — turns off the link and removes it from the playstore
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
